import * as z from "zod";

export const customEmailProviderSchema = z.enum(["postmark", "ses"]);
export type CustomEmailProvider = z.infer<typeof customEmailProviderSchema>;

const sesReadinessStepSchema = z.enum([
  "no_domain",
  "domain_not_linked",
  "dns_pending",
  "dkim_failed",
  "ready",
]);
export type SesReadinessStep = z.infer<typeof sesReadinessStepSchema>;

/** A DNS record for the sender's SES domain, built from the stored `email_domain`
 * row; `status` is Redo's last stored DNS check, or null before one ran. */
const sesDnsRecordSchema = z.object({
  type: z.enum(["TXT", "CNAME", "MX"]),
  /** Fully qualified host, e.g. `redo._domainkey.mail.example.com`. */
  host: z.string(),
  /** The host relative to the registrable domain, for providers that append it. `@` for the apex. */
  hostPrefix: z.string(),
  value: z.string(),
  /** MX priority; null for other record types. */
  priority: z.number().nullable(),
  purpose: z.string(),
  /** False for records that help deliverability but don't gate SES sending. */
  required: z.boolean(),
  status: z.string().nullable(),
});
export type SesDnsRecord = z.infer<typeof sesDnsRecordSchema>;

/** Where a sender is in getting its domain ready for SES, step by step. */
const sesReadinessSchema = z.object({
  step: sesReadinessStepSchema,
  /** The sender's domain: everything after the `@`, lowercased. */
  domain: z.string(),
  emailDomainId: z.string().nullable(),
  linkedToTeam: z.boolean(),
  dkimStatus: z.string().nullable(),
  mailFromDomain: z.string().nullable(),
  mailFromDomainStatus: z.string().nullable(),
  dnsLastCheckedAt: z.date().nullable(),
  dnsRecords: z.array(sesDnsRecordSchema),
});
export type SesReadiness = z.infer<typeof sesReadinessSchema>;

/** A helpdesk custom email sender and whether it can send through SES. */
export const customEmailSenderSchema = z.object({
  integrationId: z.string(),
  email: z.string(),
  name: z.string(),
  setupStatus: z.string(),
  provider: customEmailProviderSchema,
  receiveEmail: z.string().nullable(),
  emailDomain: z
    .object({
      id: z.string(),
      domain: z.string(),
      dkimStatus: z.string(),
      mailFromDomainStatus: z.string(),
    })
    .nullable(),
  sesEligible: z.boolean(),
  sesIneligibleReason: z.string().nullable(),
  sesReadiness: sesReadinessSchema,
});
export type CustomEmailSender = z.infer<typeof customEmailSenderSchema>;
